'use client';

import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionTitle from '@/components/ui/SectionTitle';
import type { InventoryStatus } from '@/lib/mock-data';
import { Search, Download, RefreshCw, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';

type SortField = 'description' | 'warehouse' | 'quantity' | 'status' | 'lastUpdated';
type SortDir = 'asc' | 'desc';

export default function InventoryPage() {
  const { inventory, profile } = useCountry();
  const { t, fmtNum, fmtDate } = useLanguage();

  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<InventoryStatus | ''>('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('description');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Status filters — translated at render time
  const statusFilters = useMemo(() => [
    { id: 'sf-all', label: t('filter.allStatus'), value: '' as InventoryStatus | '' },
    { id: 'sf-in', label: t('status.in_stock'), value: 'in_stock' as InventoryStatus },
    { id: 'sf-low', label: t('status.low_stock'), value: 'low_stock' as InventoryStatus },
    { id: 'sf-out', label: t('status.out_of_stock'), value: 'out_of_stock' as InventoryStatus },
  ], [t]);

  // Build warehouse filters from current country profile
  const warehouseFilters = useMemo(() => [
    { id: 'filter-all', label: t('filter.allWarehouses'), value: '' },
    ...profile.warehouses.map((wh, i) => ({
      id: `filter-wh-${i}`,
      label: wh,
      value: wh,
    })),
  ], [profile.warehouses, t]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let items = [...inventory];
    if (warehouseFilter) items = items.filter((i) => i.warehouse === warehouseFilter);
    if (statusFilter) items = items.filter((i) => i.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (i) =>
          // Search against underlying English values (itemCode, category) as well as displayed text
          i.itemCode.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q) ||
          t(`material.${i.itemCode}`).toLowerCase().includes(q) ||
          t(`category.${i.category}`).toLowerCase().includes(q)
      );
    }
    items.sort((a, b) => {
      let va: string | number = a[sortField];
      let vb: string | number = b[sortField];
      if (sortField === 'quantity') {
        va = a.quantity;
        vb = b.quantity;
        return sortDir === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number);
      }
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return items;
  }, [inventory, warehouseFilter, statusFilter, search, sortField, sortDir, t]);

  const SortIcon = ({ field }: { field: SortField }) => (
    <ArrowUpDown
      size={12}
      className={`inline ml-1 transition-opacity ${
        sortField === field ? 'opacity-100' : 'opacity-30'
      }`}
    />
  );

  const stockSummary = {
    inStock: inventory.filter((i) => i.status === 'in_stock').length,
    lowStock: inventory.filter((i) => i.status === 'low_stock').length,
    outOfStock: inventory.filter((i) => i.status === 'out_of_stock').length,
  };

  return (
    <AppLayout
      title={t('page.inventoryTitle')}
      subtitle={t('page.inventorySub')}
    >
      {/* Summary Strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: t('status.in_stock'), count: stockSummary.inStock, color: 'var(--status-green)' },
          { label: t('status.low_stock'), count: stockSummary.lowStock, color: 'var(--status-amber)' },
          { label: t('status.out_of_stock'), count: stockSummary.outOfStock, color: 'var(--status-red)' },
        ].map((s) => (
          <div
            key={`summary-${s.label}`}
            className="bg-surface-card rounded-xl px-4 py-3 border border-surface-border shadow-card flex items-center gap-3"
          >
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-sm font-semibold text-text-main tabular-nums">
              {s.count}
            </span>
            <span className="text-xs text-text-muted">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Main Card */}
      <div className="bg-surface-card rounded-xl border border-surface-border shadow-card">
        <div className="p-5 border-b border-surface-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <SectionTitle
              title="Warehouse Stock Register"
              subtitle={`${filtered.length} ${t('common.of')} ${inventory.length} ${t('common.results')}`}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => toast.success('Inventory data refreshed')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-muted hover:text-text-main border border-surface-border rounded-lg hover:bg-surface-light transition-colors"
              >
                <RefreshCw size={12} />
                Refresh
              </button>
              <button
                onClick={() => toast.success(t('toast.exported'))}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors hover:opacity-90"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                <Download size={12} />
                {t('action.export')} CSV
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mt-3">
            {/* Warehouse pills */}
            <div className="flex flex-wrap gap-1.5">
              {warehouseFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setWarehouseFilter(f.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
                    warehouseFilter === f.value
                      ? 'text-white border-transparent' : 'text-text-muted border-surface-border hover:border-accent/50 hover:text-text-main bg-surface-light'
                  }`}
                  style={
                    warehouseFilter === f.value
                      ? { backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }
                      : {}
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="w-px self-stretch bg-surface-border mx-1 hidden sm:block" />

            {/* Status pills */}
            <div className="flex flex-wrap gap-1.5">
              {statusFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.value as InventoryStatus | '')}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
                    statusFilter === f.value
                      ? 'text-white border-transparent' : 'text-text-muted border-surface-border hover:border-accent/50 hover:text-text-main bg-surface-light'
                  }`}
                  style={
                    statusFilter === f.value
                      ? { backgroundColor: 'var(--accent)', borderColor: 'var(--accent)' }
                      : {}
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="relative mt-3 max-w-sm">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="text"
              placeholder="Search by item name, code, or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-surface-light border border-surface-border text-sm rounded-lg pl-8 pr-3 py-1.5 text-text-main placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {[
                  { key: 'itemCode', label: t('table.itemCode'), sortable: false },
                  { key: 'description', label: t('table.description'), sortable: true },
                  { key: 'category', label: t('table.category'), sortable: false },
                  { key: 'warehouse', label: t('table.warehouse'), sortable: true },
                  { key: 'quantity', label: t('table.quantity'), sortable: true },
                  { key: 'unit', label: t('table.unit'), sortable: false },
                  { key: 'reorderLevel', label: t('table.reorderLevel'), sortable: false },
                  { key: 'status', label: t('table.status'), sortable: true },
                  { key: 'lastUpdated', label: t('table.lastUpdated'), sortable: true },
                ].map((col) => (
                  <th
                    key={`inv-th-${col.key}`}
                    className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-text-muted ${
                      col.sortable ? 'cursor-pointer hover:text-text-main' : ''
                    }`}
                    style={{ letterSpacing: '0.06em', backgroundColor: 'var(--background)' }}
                    onClick={
                      col.sortable
                        ? () => handleSort(col.key as SortField)
                        : undefined
                    }
                  >
                    {col.label}
                    {col.sortable && <SortIcon field={col.key as SortField} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Search size={32} className="text-text-muted opacity-30" />
                      <p className="text-sm font-medium text-text-muted">
                        {t('common.noResults')}
                      </p>
                      <p className="text-xs text-text-muted">
                        Try adjusting warehouse, status, or search term
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((item, idx) => (
                  <tr
                    key={item.id}
                    className="transition-colors hover:bg-surface-light group"
                    style={{
                      borderBottom:
                        idx < filtered.length - 1
                          ? '1px solid var(--border)'
                          : 'none',
                    }}
                  >
                    <td className="py-3 px-4">
                      <span
                        className="font-mono text-xs font-semibold"
                        style={{ color: 'var(--primary)' }}
                      >
                        {item.itemCode}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-text-main max-w-[200px]">
                      <span className="truncate block">{t(`material.${item.itemCode}`)}</span>
                    </td>
                    <td className="py-3 px-4 text-text-muted text-xs">
                      {t(`category.${item.category}`)}
                    </td>
                    <td className="py-3 px-4 text-xs text-text-muted whitespace-nowrap">
                      {item.warehouse}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`font-bold tabular-nums text-sm ${
                          item.quantity === 0
                            ? 'text-status-red'
                            : item.quantity <= item.reorderLevel
                            ? 'text-status-amber' : 'text-text-main'
                        }`}
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {fmtNum(item.quantity)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-text-muted">
                      {t(`common.${item.unit}`)}
                    </td>
                    <td className="py-3 px-4 text-xs text-text-muted tabular-nums">
                      {fmtNum(item.reorderLevel)}
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={item.status} label={t(`status.${item.status}`)} />
                    </td>
                    <td className="py-3 px-4 text-xs text-text-muted whitespace-nowrap">
                      {fmtDate(item.lastUpdated)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div
          className="px-5 py-3 flex items-center justify-between text-xs text-text-muted"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <span>
            {t('common.showing')} <span className="font-semibold text-text-main">{filtered.length}</span> {t('common.of')}{' '}
            <span className="font-semibold text-text-main">{inventory.length}</span> {t('common.results')}
          </span>
          <span>
            Last sync: <span className="font-medium text-text-main">24 Jul 2026, 03:14 WAT</span>
          </span>
        </div>
      </div>
    </AppLayout>
  );
}